import { Link } from "react-router-dom";
import { MapPin, Clock, Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { offeringPrice, typeLabel, initials } from "@/lib/format";
import { useT } from "@/i18n";

export function OfferingCard({ offering }) {
  const t = useT();
  const coach = offering.coach || {};
  return (
    <Link
      to={`/marketplace/${offering.id}`}
      data-testid={`offering-card-${offering.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(44,44,42,0.45)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-oat-200">
        {offering.cover_image_url ? (
          <img
            src={offering.cover_image_url}
            alt={offering.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-accent text-accent-foreground">
            <Package className="h-8 w-8 opacity-40" />
          </div>
        )}
        <div className="absolute left-3 top-3 flex gap-2">
          <Badge className="rounded-full bg-background/90 text-foreground hover:bg-background/90">
            {typeLabel(offering)}
          </Badge>
          {offering.promoted ? (
            <Badge className="rounded-full bg-clay/90 text-white hover:bg-clay/90">{t("oc.recommended")}</Badge>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="line-clamp-2 font-display text-lg leading-snug text-foreground">
          {offering.title}
        </h3>
        <p className="line-clamp-2 flex-1 text-sm text-muted-foreground">
          {offering.description}
        </p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {offering.duration_minutes ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {t("oc.minutes", { n: offering.duration_minutes })}
            </span>
          ) : null}
          {offering.region ? (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {offering.region}
            </span>
          ) : null}
          {offering.is_bundle ? (
            <span className="inline-flex items-center gap-1">
              <Package className="h-3.5 w-3.5" /> {t("oc.parts", { n: offering.bundle_item_count })}
            </span>
          ) : null}
        </div>

        <div className="mt-1 flex items-center justify-between border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Avatar className="h-7 w-7">
              <AvatarImage src={coach.photo} alt={coach.name} />
              <AvatarFallback className="bg-sage-pale text-[10px] text-sage-deep">
                {initials(coach.name)}
              </AvatarFallback>
            </Avatar>
            <span className="max-w-[120px] truncate text-xs font-medium text-foreground">
              {coach.name}
            </span>
          </div>
          <span className="font-display text-lg font-semibold text-primary">
            {offeringPrice(offering)}
          </span>
        </div>
      </div>
    </Link>
  );
}
